/** @jsxImportSource @emotion/react */
import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { costFormatter } from "@phoenix/utils/numberFormatUtils";
import { ChartTypeRenderer, type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import type { TopKeyAliasesByCostQuery } from "./__generated__/TopKeyAliasesByCostQuery.graphql";

const FALLBACK_LABEL = "(unknown)";
const MAX_ITEMS = 10;
const COLOR_PALETTE = ["#059669", "#10b981", "#34d399", "#6ee7b7", "#047857", "#065f46", "#15803d", "#22c55e", "#4ade80", "#86efac"];

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

export function TopKeyAliasesByCost({ projectId, timeRange, chartType = "vertical_bar", widgetId, widgetTitle, onDataExport }: Props) {
  const data = useLazyLoadQuery<TopKeyAliasesByCostQuery>(
    graphql`
      query TopKeyAliasesByCostQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, rootSpansOnly: true, first: 500) {
              edges {
                span: node {
                  keyAlias
                  metadata
                  trace { costSummary { total { cost } } }
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
    }
  );

  const chartData = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      let key = span.keyAlias;
      if (!key && span.metadata) {
        try {
          const meta = typeof span.metadata === "string" ? JSON.parse(span.metadata) : span.metadata;
          // Only use user_api_key_alias - never fall back to user_api_key_hash (security: hash could expose key info)
          key = meta.user_api_key_alias ?? null;
        } catch {
          // ignore
        }
      }
      const label = key || FALLBACK_LABEL;
      map[label] = (map[label] ?? 0) + (span.trace?.costSummary?.total?.cost ?? 0);
    }
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);

  return (
    <ChartTypeRenderer
      data={chartData}
      chartType={chartType}
      valueFormatter={costFormatter}
      colorPalette={COLOR_PALETTE}
      emptyIcon="🔑"
      emptyText="No Key Alias cost data"
    />
  );
}
